import { Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { convertGraphqlArpyFileToArpyRequirementFile } from 'src/app/graphQL/mappers/arpy-file.mapper';
import { ArpyVehicleFunctionParseResult, convertGraphqlArpyVehicleFunctionCatalog } from 'src/app/graphQL/mappers/arpy-vehicle-function-catalog.mapper';
import { GraphQLArpyDownloadURLResponseOutput, GraphQLArpyFileResponseOutput, GraphQLArpyListFilesResponseOutput } from 'src/app/graphQL/models/output/graphql-arpy-file-output';
import { GraphQLArpyVehicleFunctionCatalogResponseOutput } from 'src/app/graphQL/models/output/graphql-arpy-vehicle-function-catalog-output';
import { GET_ARPY_DOWNLOAD_URL_MUTATION, GET_ARPY_REQUIREMENT_FILE_QUERY, GET_ARPY_VEHICLE_FUNCTION_CATALOG_QUERY, LIST_ARPY_REQUIREMENT_FILES_QUERY } from 'src/app/graphQL/queries/arpy-file-queries';
import { ArpyRequirementFile, ArpyRequirementSourceType } from 'src/app/models/arpyRequirementFile';
import { ApolloClientService } from './apollo-client.service';
import { environment } from 'src/environments/environment';

const EMPTY_VEHICLE_FUNCTIONS: ArpyVehicleFunctionParseResult = {
  vehicleFunctions: [],
  vehicleSubFunctions: [],
  requirements: [],
};

@Injectable({
  providedIn: 'root'
})
export class ArpyApiService {

  constructor(private apolloClientService: ApolloClientService) { }

  // Lists file metadata (not file contents) for Arpy source files, newest
  // first. Pass a sourceType (e.g. 'TEST') to filter to one source, or
  // omit it to list every source - use this to populate a file browser/list view.
  listRequirementFiles(sourceType?: ArpyRequirementSourceType): Observable<ArpyRequirementFile[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{ arpyListRequirementFiles: GraphQLArpyListFilesResponseOutput }>({
          query: LIST_ARPY_REQUIREMENT_FILES_QUERY,
          variables: { sourceType }
        })
      ),
      map(result => {
        const files = result.data.arpyListRequirementFiles?.data
        if (!files)
          return []

        return files.map(file => convertGraphqlArpyFileToArpyRequirementFile(file))
      })
    )
  }

  // Fetches a single file's metadata by its id, e.g. to show file details
  // before downloading it. The API reports an unknown id via success=false
  // rather than an error, so this resolves to undefined - check for that
  // before using the result.
  getRequirementFile(id: string): Observable<ArpyRequirementFile> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{ arpyGetRequirementFile: GraphQLArpyFileResponseOutput }>({
          query: GET_ARPY_REQUIREMENT_FILE_QUERY,
          variables: { id }
        })
      ),
      map(result => {
        const file = result.data.arpyGetRequirementFile?.data
        if (!file)
          return undefined

        return convertGraphqlArpyFileToArpyRequirementFile(file)
      })
    )
  }

  // Mints a presigned S3 GET url so the client can download a file's bytes
  // directly from S3 (bytes never pass through the abstraction layer). The
  // url expires in `expiresIn` seconds (5 minutes) - request it right before
  // the download starts rather than caching it. Resolves to undefined for an
  // unknown or soft-deleted (inactive) file.
  getDownloadUrl(id: string): Observable<{ url: string; expiresIn: number }> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{ arpyGetDownloadURL: GraphQLArpyDownloadURLResponseOutput }>({
          mutation: GET_ARPY_DOWNLOAD_URL_MUTATION,
          variables: { id }
        })
      ),
      map(result => result.data?.arpyGetDownloadURL?.data)
    )
  }

  // Builds the vehicle-function / sub-function / requirement catalog from the
  // latest TEESA file, parsed server-side by arpy-service-lambda
  // (arpyGetVehicleFunctionCatalog) - one small response instead of
  // downloading and parsing the raw (20+ MB on real exports) file in the
  // browser. Call this once and cache the result (see
  // ArpyVehicleFunctionsState) rather than from a component that
  // re-subscribes often.
  //
  // The result carries the source file's own `version` alongside the parsed
  // catalog: any link created from this catalog is stamped with it, so an
  // entity that later disappears from a newer "latest" file can still be
  // traced back to the exact file version it was linked from.
  getAllVehicleFunctions(): Observable<ArpyVehicleFunctionParseResult & { sourceFileVersion?: number }> {
    // Same guard as VehicleFunctionsApiService.get() - Arpy is not part of the Hero
    // environment, at all, so skip the fetch entirely rather than fetching a
    // potentially large TEESA file for a tenant that will never use it.
    if (environment.IsHero)
      return of(EMPTY_VEHICLE_FUNCTIONS);

    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{ arpyGetVehicleFunctionCatalog: GraphQLArpyVehicleFunctionCatalogResponseOutput }>({
          query: GET_ARPY_VEHICLE_FUNCTION_CATALOG_QUERY
        })
      ),
      map(result => {
        const response = result.data.arpyGetVehicleFunctionCatalog;
        if (!response?.success || !response.data)
          throw new Error(response?.message ?? 'arpyGetVehicleFunctionCatalog returned no data');

        return convertGraphqlArpyVehicleFunctionCatalog(response.data);
      })
    );
  }
}
